import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fmtDateTime } from '@tww/shared';
import { api } from '../../api/client';
import { ErrorState, Note, Panel, Skeleton, StatusPill } from '../../ui/basics';
import { ConfirmDialog } from '../../ui/Modal';
import { useToast } from '../../ui/Toast';
import '../../styles/import.css';
import { Dropdown } from '../../ui/Dropdown';

/* ---------- Typen (Spiegel der API) ---------- */
interface FieldSpec {
  key: string;
  label: string;
  columns: string[];
  required?: boolean;
}
interface SourceInfo {
  type: string;
  label: string;
  description: string;
  sheets: { key: string; label: string; fields: FieldSpec[] }[];
}
interface SheetInfo {
  key: string | null;
  label: string | null;
  name: string;
  header_row: number;
  headers: string[];
  rows: number;
}
interface Issue {
  field: string | null;
  value: string | null;
  problem: string;
  suggestion?: string | null;
}
interface BatchDetail {
  id: number;
  source_file: string;
  source_type: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  summary: {
    sheets: SheetInfo[];
    mapping: Record<string, Record<string, string | null>>;
    rows: number;
    dry_run: { at: string; total: number; new: number; update: number; review: number; skip: number } | null;
    result: { at: string; imported: number; review: number; skipped: number; errors: number } | null;
  };
  source: SourceInfo;
  preview: Record<string, { row_no: number; cells: Record<string, unknown> }[]>;
  issues: { id: number; row_no: number; sheet: string; status: string; target_id: number | null; target_type: string | null; label: string | null; issues: Issue[]; skip: string | null }[];
  counts: Record<string, number>;
}
interface BatchListItem {
  id: number;
  source_file: string;
  source_type: string;
  status: string;
  started_at: string;
  summary: BatchDetail['summary'] | null;
}

const STATUS: Record<string, { code: string; label: string }> = {
  staged: { code: 'abstimmung', label: 'vorbereitet' },
  running: { code: 'abstimmung', label: 'läuft' },
  done: { code: 'bestaetigt', label: 'übernommen' },
  failed: { code: 'offen', label: 'fehlgeschlagen' },
  cancelled: { code: 'muted', label: 'verworfen' },
};
const ROW_STATUS: Record<string, { code: string; label: string }> = {
  staged: { code: 'muted', label: 'wird übernommen' },
  review: { code: 'warn', label: 'zu prüfen' },
  imported: { code: 'bestaetigt', label: 'übernommen' },
  skipped: { code: 'muted', label: 'übersprungen' },
  error: { code: 'offen', label: 'Fehler' },
};
const TEMPLATES = [
  { type: 'contacts', label: 'Kontakte (fünf Reiter)' },
  { type: 'actions', label: 'Aktionen (Pflanzplan)' },
  { type: 'areas', label: 'Flächen mit Monitoring' },
];

const recordLink = (type: string | null, id: number | null) => (id && type === 'contact' ? `/kontakte/${id}` : id && type === 'action' ? `/aktionen/${id}` : null);
const cellStr = (v: unknown) => (v == null ? '' : v instanceof Date ? v.toISOString().slice(0, 10) : String(v));

/** Verwaltung › Import: Datei einlesen, Zuordnung prüfen, Trockenlauf, Prüfbericht, Übernahme, Vorlagen */
export function ImportPage() {
  const [sp, setSp] = useSearchParams();
  const batchId = Number(sp.get('import') ?? 0) || null;
  const open = (id: number | null) => setSp(id ? { import: String(id) } : {}, { replace: true });
  return batchId ? <BatchView id={batchId} onBack={() => open(null)} /> : <StartView onOpen={open} />;
}

function StartView({ onOpen }: { onOpen: (id: number) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const sources = useQuery({ queryKey: ['import-sources'], queryFn: () => api.get<{ items: SourceInfo[] }>('/import/sources').then((r) => r.items) });
  const batches = useQuery({ queryKey: ['import-batches'], queryFn: () => api.get<{ items: BatchListItem[] }>('/import/batches').then((r) => r.items) });
  const [type, setType] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const create = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.set('source_type', type);
      fd.set('file', file!);
      return api.upload<BatchDetail>('/import/batches', fd);
    },
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ['import-batches'] });
      onOpen(b.id);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const src = sources.data?.find((s) => s.type === type);
  return (
    <>
      <Panel title="Neuer Import">
        <div className="pb">
          <div className="grid" style={{ gridTemplateColumns: 'minmax(220px, 1fr) minmax(220px, 1fr) auto', alignItems: 'end' }}>
            <div className="field">
              <label htmlFor="imp-type">Quelle</label>
              <Dropdown id="imp-type" value={type} onChange={(v) => setType(v)} placeholder="– wählen –" options={(sources.data ?? []).map((s) => ({ value: String(s.type), label: s.label }))} />
              {src && <span className="hint">{src.description}</span>}
            </div>
            <div className="field">
              <label htmlFor="imp-file">Datei (.xlsx oder .csv)</label>
              <input id="imp-file" type="file" accept=".xlsx,.xlsm,.csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </div>
            <button type="button" className="btn primary" disabled={!type || !file || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Wird gelesen …' : 'Einlesen und prüfen'}
            </button>
          </div>
          <div className="mut small mt-8">Die Datei wird nur vorbereitet. Übernommen wird erst nach dem Trockenlauf mit einem eigenen Knopf.</div>
        </div>
      </Panel>
      <Panel title="Importvorlagen">
        <div className="pb">
          <div className="mut small mb-6">Leere Vorlagen mit Kopfzeile, Beispielzeile und Auswahllisten. Ausgefüllt lassen sie sich oben als Quelle „Kontakte“ beziehungsweise „Pflanzplan“ einlesen.</div>
          <div className="imp-kpis">
            {TEMPLATES.map((t) => (
              <a key={t.type} className="btn" href={`/api/v1/import/templates/${t.type}`}>
                {t.label}
              </a>
            ))}
          </div>
        </div>
      </Panel>
      <Panel title="Bisherige Importe">
        {batches.isLoading ? (
          <Skeleton rows={3} />
        ) : batches.data?.length ? (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Datei</th>
                  <th>Quelle</th>
                  <th>Datum</th>
                  <th>Status</th>
                  <th>Ergebnis</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {batches.data.map((b) => (
                  <tr key={b.id}>
                    <td>{b.source_file}</td>
                    <td className="small">{sources.data?.find((s) => s.type === b.source_type)?.label ?? b.source_type}</td>
                    <td className="nowrap">{fmtDateTime(b.started_at)}</td>
                    <td>
                      <StatusPill code={STATUS[b.status]?.code ?? 'muted'} label={STATUS[b.status]?.label ?? b.status} />
                    </td>
                    <td className="small">{resultText(b.summary)}</td>
                    <td>
                      {b.status !== 'cancelled' && (
                        <button type="button" className="btn sm" onClick={() => onOpen(b.id)}>
                          Öffnen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">Noch kein Import</div>
        )}
      </Panel>
    </>
  );
}

function resultText(s: BatchDetail['summary'] | null): string {
  if (!s) return '';
  if (s.result) return `${s.result.imported} übernommen · ${s.result.review} zu prüfen · ${s.result.errors} Fehler`;
  if (s.dry_run) return `${s.dry_run.total} Zeilen · ${s.dry_run.new} neu · ${s.dry_run.update} ergänzen · ${s.dry_run.review} zu prüfen`;
  return `${s.rows} Zeilen`;
}

function BatchView({ id, onBack }: { id: number; onBack: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['import-batch', id], queryFn: () => api.get<BatchDetail>(`/import/batches/${id}`) });
  const [confirm, setConfirm] = useState<'commit' | 'cancel' | null>(null);
  const done = () => {
    qc.invalidateQueries({ queryKey: ['import-batch', id] });
    qc.invalidateQueries({ queryKey: ['import-batches'] });
  };
  const mapping = useMutation({
    mutationFn: (m: Record<string, Record<string, string | null>>) => api.patch<BatchDetail>(`/import/batches/${id}/mapping`, m),
    onSuccess: (b) => {
      qc.setQueryData(['import-batch', id], b);
      toast('Zuordnung übernommen, Prüfung wiederholt');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const dry = useMutation({ mutationFn: () => api.post<BatchDetail>(`/import/batches/${id}/dry-run`), onSuccess: (b) => (qc.setQueryData(['import-batch', id], b), toast('Prüfung wiederholt')), onError: (e: Error) => toast(e.message, 'error') });
  const commit = useMutation({
    mutationFn: () => api.post<BatchDetail>(`/import/batches/${id}/commit`),
    onSuccess: (b) => {
      qc.setQueryData(['import-batch', id], b);
      qc.invalidateQueries({ queryKey: ['import-batches'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['actions'] });
      setConfirm(null);
      toast(`${b.summary.result?.imported ?? 0} Datensätze übernommen`);
    },
    onError: (e: Error) => (setConfirm(null), toast(e.message, 'error')),
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/import/batches/${id}/cancel`),
    onSuccess: () => (done(), setConfirm(null), toast('Import verworfen'), onBack()),
    onError: (e: Error) => toast(e.message, 'error'),
  });

  if (q.isLoading) return <Skeleton rows={6} />;
  if (q.error || !q.data) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  const b = q.data;
  const s = b.summary;
  const editable = b.status === 'staged';
  const mappedSheets = s.sheets.filter((sh) => sh.key);
  const unmapped = s.sheets.filter((sh) => !sh.key && sh.headers.length);
  const missingRequired = mappedSheets.flatMap((sh) => {
    const spec = b.source.sheets.find((x) => x.key === sh.key);
    return (spec?.fields ?? []).filter((f) => f.required && !s.mapping[sh.key!]?.[f.key]).map((f) => `${sh.name}: ${f.label}`);
  });

  return (
    <>
      <div className="imp-row mb-6">
        <div>
          <button type="button" className="btn sm" onClick={onBack}>
            ← Alle Importe
          </button>
        </div>
        <div className="imp-row">
          <StatusPill code={STATUS[b.status]?.code ?? 'muted'} label={STATUS[b.status]?.label ?? b.status} />
          <a className="btn sm" href={`/api/v1/import/batches/${b.id}/report`}>
            Prüfbericht (Excel)
          </a>
          {editable && (
            <button type="button" className="btn sm" onClick={() => setConfirm('cancel')}>
              Verwerfen
            </button>
          )}
        </div>
      </div>
      <Panel title={`${b.source_file} · ${b.source.label}`}>
        <div className="pb">
          <div className="imp-kpis">
            <div className="k">
              <b>{s.rows}</b>
              <span>Zeilen</span>
            </div>
            {s.dry_run && (
              <>
                <div className="k">
                  <b>{s.dry_run.new}</b>
                  <span>neu</span>
                </div>
                <div className="k">
                  <b>{s.dry_run.update}</b>
                  <span>ergänzen Bestehende</span>
                </div>
                <div className="k">
                  <b>{s.dry_run.review}</b>
                  <span>zu prüfen</span>
                </div>
                <div className="k">
                  <b>{s.dry_run.skip}</b>
                  <span>übersprungen</span>
                </div>
              </>
            )}
            {s.result && (
              <>
                <div className="k">
                  <b>{s.result.imported}</b>
                  <span>übernommen</span>
                </div>
                <div className="k">
                  <b>{s.result.errors}</b>
                  <span>Fehler</span>
                </div>
              </>
            )}
          </div>
          {unmapped.length > 0 && <Note>Nicht berücksichtigte Reiter: {unmapped.map((u) => u.name).join(', ')}.</Note>}
          {missingRequired.length > 0 && <Note kind="bad">Pflichtspalten nicht zugeordnet: {missingRequired.join(' · ')}. Bitte unten zuordnen.</Note>}
          {s.result && (
            <Note kind="good">
              Übernommen am {fmtDateTime(s.result.at)}. Datensätze mit Hinweisen tragen das Kennzeichen „zu prüfen“ und erscheinen über den Filter „zu prüfen“ in{' '}
              <Link className="link" to="/kontakte?f.review_needed=1">
                Kontakten
              </Link>{' '}
              und{' '}
              <Link className="link" to="/aktionen?f.review_needed=1">
                Aktionen
              </Link>
              .
            </Note>
          )}
        </div>
        {editable && (
          <div className="pb pb-top row-between">
            <div className="mut small">Erst prüfen, dann übernehmen. Die Übernahme ergänzt bestehende Datensätze nur in leeren Feldern.</div>
            <div className="imp-row">
              <button type="button" className="btn" onClick={() => dry.mutate()} disabled={dry.isPending}>
                Prüfung wiederholen
              </button>
              <button type="button" className="btn primary" onClick={() => setConfirm('commit')} disabled={!s.dry_run || missingRequired.length > 0 || commit.isPending}>
                {commit.isPending ? 'Übernahme läuft …' : 'Übernehmen'}
              </button>
            </div>
          </div>
        )}
      </Panel>

      {mappedSheets.map((sh) => {
        const spec = b.source.sheets.find((x) => x.key === sh.key)!;
        const m = s.mapping[sh.key!] ?? {};
        const rows = b.preview[sh.key!] ?? [];
        const shown = spec.fields.filter((f) => m[f.key]);
        return (
          <Panel key={sh.name} title={`Reiter „${sh.name}“ · ${sh.rows} Zeilen · Kopfzeile in Zeile ${sh.header_row}`}>
            <div className="pb">
              <div className="mut small mb-6">Zuordnung der Spalten zu den Feldern der Datenbank. Nicht zugeordnete Felder bleiben leer.</div>
              <div className="imp-map">
                {spec.fields.map((f) => (
                  <div key={f.key} className={`field ${f.required && !m[f.key] ? 'error' : ''}`}>
                    <label htmlFor={`m-${sh.key}-${f.key}`}>
                      {f.label}
                      {f.required && <span className="req"> *</span>}
                    </label>
                    <Dropdown id={`m-${sh.key}-${f.key}`} value={m[f.key] ?? ''} disabled={!editable || mapping.isPending} onChange={(v) => mapping.mutate({ [sh.key!]: { [f.key]: v || null } })} placeholder="– nicht zugeordnet –" options={sh.headers.map((h) => ({ value: String(h), label: h }))} />
                  </div>
                ))}
              </div>
            </div>
            {rows.length > 0 && shown.length > 0 && (
              <div className="pb pb-top">
                <div className="mut small mb-6">Vorschau der ersten {rows.length} Zeilen</div>
                <div className="tw">
                  <table>
                    <thead>
                      <tr>
                        <th>Zeile</th>
                        {shown.slice(0, 10).map((f) => (
                          <th key={f.key}>{f.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.row_no}>
                          <td className="mut">{r.row_no}</td>
                          {shown.slice(0, 10).map((f) => (
                            <td key={f.key} className="small">
                              {cellStr(r.cells[m[f.key]!]).slice(0, 60)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Panel>
        );
      })}

      <Panel title={`Hinweise (${b.issues.length})`}>
        {b.issues.length ? (
          <div className="tw imp-issues">
            <table>
              <thead>
                <tr>
                  <th>Zeile</th>
                  <th>Reiter</th>
                  <th>Datensatz</th>
                  <th>Hinweise</th>
                  <th>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {b.issues.map((r) => {
                  const link = recordLink(r.target_type, r.target_id);
                  return (
                    <tr key={r.id}>
                      <td className="mut">{r.row_no}</td>
                      <td className="small">{r.sheet}</td>
                      <td>{link ? <Link className="link" to={link}>{r.label ?? `#${r.target_id}`}</Link> : r.label}</td>
                      <td className="small">
                        {r.skip && <div className="mut">übersprungen: {r.skip}</div>}
                        <ul>
                          {r.issues.map((i, idx) => (
                            <li key={idx}>
                              {i.problem}
                              {i.suggestion && <span className="mut"> – {i.suggestion}</span>}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <StatusPill code={ROW_STATUS[r.status]?.code ?? 'muted'} label={ROW_STATUS[r.status]?.label ?? r.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">{s.dry_run ? 'Keine Hinweise, alle Zeilen sind eindeutig.' : 'Noch nicht geprüft.'}</div>
        )}
      </Panel>

      {confirm === 'commit' && (
        <ConfirmDialog
          title="Daten übernehmen"
          text={`${s.dry_run?.new ?? 0} neue Datensätze anlegen und ${s.dry_run?.update ?? 0} bestehende ergänzen? ${s.dry_run?.review ?? 0} davon werden als „zu prüfen“ markiert.`}
          confirmLabel="Übernehmen"
          onConfirm={() => commit.mutate()}
          onClose={() => setConfirm(null)}
          busy={commit.isPending}
        />
      )}
      {confirm === 'cancel' && (
        <ConfirmDialog title="Import verwerfen" text="Die vorbereiteten Zeilen werden gelöscht. Es wurde nichts übernommen." confirmLabel="Verwerfen" danger onConfirm={() => cancel.mutate()} onClose={() => setConfirm(null)} busy={cancel.isPending} />
      )}
    </>
  );
}
